import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Shield, Search, UploadCloud, VenusAndMars } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import ExportDialog from "@/components/ExportDialog";
import { CLASSES, SECTIONS, classLabel } from "@/pages/teacher/teacherUtils";
import TeachersTable from "@/pages/teacher/TeachersTable";
import TeacherFormDialog from "@/pages/teacher/TeacherFormDialog";
import GenderBulkDialog from "@/pages/teacher/GenderBulkDialog";

const EMPTY = { firstName: "", lastName: "", email: "", phone: "", password: "", role: "teacher", gender: "", assignments: [], class_teacher_of: [] };

export default function TeacherManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isReadOnly = user?.role === "super_admin";
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [subjectsByClass, setSubjectsByClass] = useState({});
  const [query, setQuery] = useState("");
  const [classTeachersOnly, setClassTeachersOnly] = useState(false);
  const [unassigned, setUnassigned] = useState([]);
  const [genderOpen, setGenderOpen] = useState(false);

  const filteredTeachers = (() => {
    const q = query.trim().toLowerCase();
    let list = teachers;
    if (classTeachersOnly) list = list.filter((t) => (t.class_teacher_of || []).length > 0);
    if (!q) return list;
    return list.filter((t) => {
      const subjects = (t.subjects && t.subjects.length ? t.subjects.join(" ") : (t.subject || ""));
      const classes = (t.class_teacher_of || []).map((a) => `${classLabel(a.class_name)}-${a.section} ${a.class_name}${a.section}`).join(" ");
      const assigns = (t.assignments || []).map((a) => `${classLabel(a.class_name)}-${a.section} ${a.class_name}${a.section}`).join(" ");
      return [t.name, t.email, t.phone, t.role, subjects, classes, assigns]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q));
    });
  })();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/teachers");
      setTeachers(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Failed to load teachers");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  // Silent presence refresh — keeps the online/away dots live without a loading flash.
  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/teachers");
        setTeachers(data);
      } catch { /* silent */ }
    }, 45_000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => {
    api.get("/admin/subjects-by-class").then(({ data }) => setSubjectsByClass(data)).catch((err) => console.warn("subjects-by-class load failed:", err));
  }, []);
  useEffect(() => {
    api.get("/admin/sections/unassigned").then(({ data }) => setUnassigned(data.sections || [])).catch((err) => console.warn("sections/unassigned load failed:", err));
  }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    const parts = (t.name || "").trim().split(/\s+/);
    setEditing(t);
    setForm({
      firstName: parts[0] || "",
      lastName: parts.slice(1).join(" ") || "",
      email: t.email,
      phone: t.phone || "",
      password: "",
      role: t.role || "teacher",
      gender: t.gender || "",
      assignments: (t.assignments || []).map((a) => ({ class_name: a.class_name, section: a.section, subjects: a.subjects || [] })),
      class_teacher_of: t.class_teacher_of || [],
    });
    setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleClassTeacher = (cn, section) => {
    setForm((f) => {
      const exists = (f.class_teacher_of || []).some((a) => a.class_name === cn && a.section === section);
      const next = exists ? [] : [{ class_name: cn, section }];
      // Class teacher's section is auto-added to their assignments too.
      let assigns = f.assignments || [];
      if (!exists && !assigns.some((a) => a.class_name === cn && a.section === section)) {
        assigns = [...assigns, { class_name: cn, section, subjects: [] }];
      }
      return { ...f, class_teacher_of: next, assignments: assigns };
    });
  };

  const toggleAssignment = (cn, section) => {
    setForm((f) => {
      const exists = (f.assignments || []).some((a) => a.class_name === cn && a.section === section);
      const next = exists
        ? f.assignments.filter((a) => !(a.class_name === cn && a.section === section))
        : [...(f.assignments || []), { class_name: cn, section, subjects: [] }];
      const ct = exists
        ? (f.class_teacher_of || []).filter((a) => !(a.class_name === cn && a.section === section))
        : (f.class_teacher_of || []);
      return { ...f, assignments: next, class_teacher_of: ct };
    });
  };
  const toggleSubject = (cn, section, subj) => {
    setForm((f) => {
      const assignments = (f.assignments || []).map((a) => {
        if (a.class_name !== cn || a.section !== section) return a;
        const has = (a.subjects || []).includes(subj);
        return { ...a, subjects: has ? a.subjects.filter((s) => s !== subj) : [...(a.subjects || []), subj] };
      });
      return { ...f, assignments };
    });
  };
  const subjectsFor = (cn, section) =>
    (form.assignments || []).find((a) => a.class_name === cn && a.section === section)?.subjects || [];
  const hasAssignment = (cn, section) =>
    (form.assignments || []).some((a) => a.class_name === cn && a.section === section);

  const save = async () => {
    const name = `${(form.firstName || "").trim()} ${(form.lastName || "").trim()}`.trim();
    if (!name) { toast.error("First name is required"); return; }
    const digits = (form.phone || "").replace(/\D/g, "").replace(/^91(?=\d{10}$)/, "").replace(/^0(?=\d{10}$)/, "");
    if (!/^[6-9]\d{9}$/.test(digits)) { toast.error("Enter a valid 10-digit Indian mobile number"); return; }
    if (!editing && !form.password) { toast.error("Password is required for new teacher"); return; }
    setSaving(true);
    try {
      const payload = { ...form, name, phone: digits };
      delete payload.firstName;
      delete payload.lastName;
      if (!payload.email) delete payload.email;
      if (editing && !payload.password) delete payload.password;
      if (editing) {
        await api.put(`/admin/teachers/${editing.id}`, payload);
        toast.success("Teacher updated");
      } else {
        await api.post("/admin/teachers", payload);
        toast.success("Teacher created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!delTarget) return;
    try {
      await api.delete(`/admin/teachers/${delTarget.id}`);
      toast.success("Teacher deleted");
      setDelTarget(null);
      load();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Administration</div>
          <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Manage teachers</h1>
          <p className="text-slate-500 mt-1">
            {isReadOnly
              ? "Read-only oversight — you can view but not modify staff records."
              : "Add, edit or remove teaching staff and admins. Assign classes per teacher."}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="relative w-full sm:max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, class, subject…"
            className="h-11 pl-9 rounded-full"
            data-testid="teacher-search-input"
          />
          {query.trim() && (
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-slate-400" data-testid="teacher-search-count">
              {filteredTeachers.length} of {teachers.length}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => setClassTeachersOnly((v) => !v)}
          className={`h-11 px-4 rounded-full text-sm font-medium inline-flex items-center gap-1.5 border transition-colors shrink-0 ${
            classTeachersOnly
              ? "bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600"
              : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
          }`}
          data-testid="class-teachers-only-toggle"
          aria-pressed={classTeachersOnly}
        >
          <Shield className="h-4 w-4" /> Class Teachers only
        </button>
        {!isReadOnly && (
          <div className="flex items-center gap-2 shrink-0">
            <ExportDialog entity="teachers" buttonLabel="Export Excel" />
            <Button
              variant="outline"
              onClick={() => setGenderOpen(true)}
              className="h-11 rounded-full"
              data-testid="set-gender-btn"
            >
              <VenusAndMars className="h-4 w-4 mr-1" /> Set gender
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/admin/settings?tab=imports&sub=teachers")}
              className="h-11 rounded-full"
              data-testid="bulk-upload-btn"
            >
              <UploadCloud className="h-4 w-4 mr-1" /> Bulk Upload
            </Button>
            <Button onClick={openCreate} className="h-11 rounded-full bg-sky-500 hover:bg-sky-600" data-testid="add-teacher-btn">
              <Plus className="h-4 w-4 mr-1" /> Add teacher
            </Button>
          </div>
        )}
      </div>

      {!isReadOnly && unassigned.length > 0 && (
        <div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-200 dark:border-amber-900 bg-amber-50 dark:bg-amber-950/30 px-4 py-3" data-testid="unassigned-sections-note">
          <Shield className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="text-sm font-medium text-amber-800 dark:text-amber-200">
              {unassigned.length} section{unassigned.length === 1 ? "" : "s"} still need a class teacher
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {unassigned.map((u) => (
                <button
                  key={`${u.class_name}-${u.section}`}
                  type="button"
                  onClick={() => navigate("/admin/settings?tab=sections")}
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-800 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
                  title="Assign a class teacher in Settings → Sections"
                  data-testid={`unassigned-${u.class_name}-${u.section}`}
                >
                  {classLabel(u.class_name)}-{u.section}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <TeachersTable
        loading={loading}
        teachers={teachers}
        filteredTeachers={filteredTeachers}
        query={query}
        isReadOnly={isReadOnly}
        user={user}
        openEdit={openEdit}
        setDelTarget={setDelTarget}
      />

      <TeacherFormDialog
        open={open}
        setOpen={setOpen}
        editing={editing}
        form={form}
        set={set}
        saving={saving}
        save={save}
        toggleClassTeacher={toggleClassTeacher}
        toggleAssignment={toggleAssignment}
        toggleSubject={toggleSubject}
        subjectsFor={subjectsFor}
        hasAssignment={hasAssignment}
        subjectsByClass={subjectsByClass}
      />

      <GenderBulkDialog
        open={genderOpen}
        setOpen={setGenderOpen}
        teachers={teachers}
        onSaved={load}
      />

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete teacher?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <b>{delTarget?.name}</b> from the system. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="del-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); remove(); }} className="bg-rose-500 hover:bg-rose-600" data-testid="del-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
